import { useEffect, useState } from "react";
import { ethers } from "ethers";
import {
  provider, DISPENSER_ABI, ERC20_ABI, fmtUnits, shortHex, type Manifest,
} from "../chain";

/**
 * The faucet is an operator tool, so it speaks in addresses and hex. That is
 * the opposite of the SettlX tab, deliberately — a counterparty should never
 * see any of this, and a developer needs all of it.
 */
export default function Faucet({ m }: { m: Manifest }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string }>();
  const [pot, setPot] = useState<{ native: bigint; tokens: bigint }>();

  const refreshPot = async () => {
    try {
      const p = provider();
      const token = new ethers.Contract(m.contracts.token, ERC20_ABI, p);
      setPot({
        native: await p.getBalance(m.contracts.dispenser),
        tokens: (await token.balanceOf(m.contracts.dispenser)) as bigint,
      });
    } catch { /* the overview already reports an unreachable chain */ }
  };

  useEffect(() => { refreshPot(); }, [m]);

  const valid = ethers.isAddress(to);

  const drip = async () => {
    setBusy(true); setMsg(undefined);
    try {
      const p = provider();
      // The faucet is permissionless and gas is zero, so anybody may call it —
      // including this page, using a throwaway key it generates and forgets.
      // No wallet extension, no key to look after, nothing to connect.
      const relay = ethers.Wallet.createRandom().connect(p);
      const dispenser = new ethers.Contract(m.contracts.dispenser, DISPENSER_ABI, relay);
      const tx = await dispenser.drip(to, { gasPrice: 0, gasLimit: 250_000 });
      const rc = await tx.wait();
      setMsg({ kind: "ok", text: `sent in block ${rc.blockNumber} — ${tx.hash}` });
      refreshPot();
    } catch (e) {
      const raw = (e as Error).message;
      const nice = /TooSoon/.test(raw)
        ? "that address was topped up recently — the cooldown has not elapsed"
        : /NothingToGive/.test(raw)
          ? "the faucet is empty and needs refilling"
          : raw;
      setMsg({ kind: "err", text: nice });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h1>Faucet</h1>
      <p className="sub">
        Sends one allocation of SX and {m.token.symbol} to any address.
        Rate-limited per recipient, not per caller — keying it by caller would let anyone
        drain it by rotating the account that asks.
      </p>

      <div className="card">
        <div className="row">
          <input
            className="mono"
            style={{ flex: "1 1 380px" }}
            placeholder="0x… recipient address"
            value={to}
            onChange={(e) => setTo(e.target.value.trim())}
            spellCheck={false}
          />
          <button className="go" onClick={drip} disabled={!valid || busy}>
            {busy ? <><span className="spin" /> sending</> : "Send funds"}
          </button>
        </div>
        {to && !valid && <p className="err" style={{ marginBottom: 0 }}>not a valid address</p>}
        {msg && (
          <p className={msg.kind === "ok" ? "ok" : "err"} style={{ marginBottom: 0, wordBreak: "break-all" }}>
            {msg.text}
          </p>
        )}
      </div>

      <h2>Per request</h2>
      <div className="grid g2">
        <div className="card stat">
          <div className="k">SX</div>
          <div className="v">{ethers.formatEther(m.dispenser.nativePerDrip)}</div>
          <div className="dim" style={{ fontSize: 13 }}>
            only because wallet UIs balk at a zero balance — gas is free here
          </div>
        </div>
        <div className="card stat">
          <div className="k">{m.token.symbol}</div>
          <div className="v">{fmtUnits(BigInt(m.dispenser.tokensPerDrip), m.token.decimals)}</div>
          <div className="dim" style={{ fontSize: 13 }}>
            cooldown {m.dispenser.cooldownSeconds}s per recipient
          </div>
        </div>
      </div>

      <h2>What is left in it</h2>
      <div className="grid g2">
        <div className="card stat">
          <div className="k">SX held</div>
          <div className="v sm">{pot ? ethers.formatEther(pot.native) : "—"}</div>
        </div>
        <div className="card stat">
          <div className="k">{m.token.symbol} held</div>
          <div className="v sm">{pot ? fmtUnits(pot.tokens, m.token.decimals) : "—"}</div>
        </div>
      </div>

      <div className="note" style={{ marginTop: 18 }}>
        This faucet holds a float and transfers from it. It is <strong>not</strong> a minter —
        <code> MINTER_ROLE</code> stays with the Issuer alone, so every unit of supply goes
        through one audited path and the faucet cannot inflate it even if misconfigured.
        It runs dry visibly instead of quietly printing money, which on a devnet is the
        behaviour you want.
        <br /><br />
        Dispenser <span className="mono">{shortHex(m.contracts.dispenser, 10, 8)}</span>
      </div>
    </>
  );
}
